import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaLocationArrow, FaBars, FaTimes, FaEnvelope, FaPhoneAlt, FaCommentDots } from "react-icons/fa";
import { AUTH_STORAGE_KEY } from "../socket";
import { getLinkedAccounts, switchAccount } from "../api/linkedAccounts";
import "./CommonNavbar.css";

const SEARCH_CITY_STORAGE_KEY = "kriva_search_city";
const CITY_OPTIONS = [
  "Delhi",
  "Mumbai",
  "Bengaluru",
  "Hyderabad",
  "Chennai",
  "Pune",
  "Kolkata",
  "Ahmedabad",
  "Jaipur",
  "Lucknow",
  "Noida",
  "Gurgaon",
  "Ghaziabad",
  "Other",
];

// ── SearchBar lives OUTSIDE CommonNavbar so it isn't recreated on every render ──
// (recreating it each render was what caused the input to lose focus after one keystroke)
function SearchBar({
  localSearch,
  setLocalSearch,
  setSearchQuery,
  onSearch,
  userLocation,
  locationLoading,
  onRequestLocation,
  onClearLocation,
  nearbyRadius,
  onSubmit,
  selectedCity,
  setSelectedCity,
  onCityChange,
  cityOptions,
}) {
  return (
    <form className="cn-search-bar" onSubmit={onSubmit}>
      <svg className="cn-search-icon" viewBox="0 0 20 20" fill="none">
        <circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor" strokeWidth="1.5" />
        <path d="M13 13l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      <input
        type="text"
        value={localSearch}
        className="cn-search-input"
        placeholder="Search business, area or category..."
        onChange={(e) => {
          const value = e.target.value;
          setLocalSearch(value);
          setSearchQuery && setSearchQuery(value);
          onSearch && onSearch(value);
        }}
      />
      <select
        className="cn-city-select"
        value={selectedCity || ""}
        onChange={(e) => {
          const value = e.target.value;
          setSelectedCity && setSelectedCity(value);
          onCityChange && onCityChange(value);
        }}
        aria-label="Select city"
      >
        <option value="">All cities</option>
        {(cityOptions || CITY_OPTIONS).map((city) => (
          <option key={city} value={city}>
            {city}
          </option>
        ))}
      </select>
      {localSearch && (
        <button
          type="button"
          className="cn-clear-btn"
          onClick={() => {
            setLocalSearch("");
            setSearchQuery && setSearchQuery("");
            onSearch && onSearch("");
          }}
          aria-label="Clear"
        >
          ✕
        </button>
      )}
      <button type="submit" className="cn-search-submit" style={{ display: "none" }}>
        Search
      </button>
      <button
        type="button"
        className={`cn-location-btn ${locationLoading ? "loading" : userLocation ? "active" : ""}`}
        onClick={userLocation ? onClearLocation : onRequestLocation}
        aria-label={userLocation ? "Clear location" : "Near me"}
      >
        <FaLocationArrow size={11} />
        <span className="cn-loc-text">
          {locationLoading ? "..." : userLocation ? `${nearbyRadius} km ✕` : "Near Me"}
        </span>
      </button>
    </form>
  );
}

export default function CommonNavbar({
  searchQuery,
  setSearchQuery,
  onSearch,
  userLocation,
  locationLoading,
  locationError,
  onRequestLocation,
  onClearLocation,
  nearbyRadius,
}) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [dropdown, setDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [switchingId, setSwitchingId] = useState(null);
  const [localSearch, setLocalSearch] = useState(searchQuery || "");
  const [selectedCity, setSelectedCity] = useState(() => {
    try {
      return localStorage.getItem(SEARCH_CITY_STORAGE_KEY) || "";
    } catch {
      return "";
    }
  });
  const dropdownRef = useRef(null);

  const getAuthHeader = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY) || "{}");
      return parsed?.token ? { Authorization: `Bearer ${parsed.token}` } : {};
    } catch {
      return {};
    }
  };

  useEffect(() => {
    setLocalSearch(searchQuery || "");
  }, [searchQuery]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const value = String(localSearch || "").trim();
    const cityValue = String(selectedCity || "").trim();
    if (!value && !cityValue) return;

    const params = new URLSearchParams();
    if (value) params.set("q", value);
    if (cityValue) params.set("city", cityValue);

    navigate(`/search?${params.toString()}`);
    onSearch && onSearch(value);
  };

  useEffect(() => {
    const data = localStorage.getItem(AUTH_STORAGE_KEY);
    if (data) {
      try {
        const parsed = JSON.parse(data);
        setUser(parsed.user || null);
        if (parsed?.token) {
          setAccountsLoading(true);
          getLinkedAccounts(getAuthHeader())
            .then((accounts) => setLinkedAccounts(accounts || []))
            .catch(() => setLinkedAccounts([]))
            .finally(() => setAccountsLoading(false));
        }
      } catch {}
    }
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    navigate("/");
    window.location.reload();
  };

  const handleSwitchAccount = async (account) => {
    if (!account?.switchToken || switchingId) return;
    setSwitchingId(account.linkId);
    try {
      const data = await switchAccount(account.switchToken, getAuthHeader());
      if (data?.success) {
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(data));
        setUser(data.user || null);
        setDropdown(false);
        setMobileMenuOpen(false);
        window.location.replace("/");
        // window.location.reload();
      } else {
        alert(data?.error || "Switch failed. Please try again.");
      }
    } catch (err) {
      console.error("Switch account failed", err);
      alert("Unable to switch account right now.");
    } finally {
      setSwitchingId(null);
    }
  };

  const initials = user
    ? (user.fullName || user.userId || "U").slice(0, 2).toUpperCase()
    : "";

  const avatarSrc = user?.profileImageUrl
    ? /^https?:\/\//i.test(user.profileImageUrl)
      ? user.profileImageUrl
      : `${window.location.origin}${user.profileImageUrl}`
    : "";

  // Shared props so we don't repeat this object twice below
  const handleCityChange = (value) => {
    const nextValue = value || "";
    setSelectedCity(nextValue);
    try {
      localStorage.setItem(SEARCH_CITY_STORAGE_KEY, nextValue);
    } catch {}
  };

  const searchBarProps = {
    localSearch,
    setLocalSearch,
    setSearchQuery,
    onSearch,
    userLocation,
    locationLoading,
    onRequestLocation,
    onClearLocation,
    nearbyRadius,
    onSubmit: handleSearchSubmit,
    selectedCity,
    setSelectedCity: handleCityChange,
    onCityChange: handleCityChange,
    cityOptions: CITY_OPTIONS,
  };

  return (
    <nav className="cn-nav">
      {/* ── main row ── */}
      <div className="cn-row1">
        {/* LEFT: logo */}
        <div className="cn-logo" onClick={() => navigate("/")}>
          <img src="/Krivaonelogo1.png" alt="Kriva Bharat" />
        </div>

        {/* CENTER: search bar (desktop only) */}
        <div className="cn-search-center">
          <SearchBar {...searchBarProps} />
          {locationError && <div className="cn-loc-error">{locationError}</div>}
        </div>

        {/* In the cn-right div, before the user/login section */}
        <div className="cn-nav-links">
          <button className="cn-nav-link" onClick={() => navigate("/kmail")}>
            <FaEnvelope size={15} /> Mail
          </button>
          <button className="cn-nav-link" onClick={() => navigate("/kmail/Krivachat")}>
            <FaCommentDots size={15} /> Chat
          </button>
          <button className="cn-nav-link" onClick={() => navigate("/kmail/calls")}>
            <FaPhoneAlt size={15} /> Call
          </button>
        </div>

        {/* RIGHT: user / login / hamburger */}
        <div className="cn-right">
          {!user && (
            <button className="cn-login-btn-mobile" onClick={() => navigate("/login")}>
              Login
            </button>
          )}

          {user && (
            <div className="cn-user-wrap" ref={dropdownRef}>
              {/* Avatar and user name */}
              <button
                className="cn-avatar"
                onClick={() => setDropdown((p) => !p)}
                title={user.fullName || user.userId}
              >
                {avatarSrc ? (
                  <img src={avatarSrc} alt="Avatar" className="cn-avatar-image" />
                ) : (
                  initials
                )}
              </button>
              <div className="cn-user-label" onClick={() => setDropdown((p) => !p)}>
                {user.fullName || user.userId}
              </div>

              {/* Dropdown menu */}
              {dropdown && (
                <div className="cn-dropdown">
                  <div className="cn-dropdown-name">{user.fullName || user.userId}</div>

                  {accountsLoading ? (
                    <div className="cn-dropdown-item">Loading accounts...</div>
                  ) : (
                    linkedAccounts.map((account) => (
                      <button
                        key={account.linkId || account.id}
                        className="cn-dropdown-item"
                        onClick={() => handleSwitchAccount(account)}
                        disabled={switchingId === account.linkId}
                      >
                        {account.fullName || account.userId}
                        {account.userId === user?.userId && " (Active)"}
                        {switchingId === account.linkId && " ..."}
                      </button>
                    ))
                  )}

                  <button className="cn-dropdown-item cn-logout" onClick={handleLogout}>
                    Logout
                  </button>
                </div>
              )}
            </div>
          )}

          <button
            className="cn-hamburger"
            onClick={() => setMobileMenuOpen((p) => !p)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <FaTimes size={18} /> : <FaBars size={18} />}
          </button>
        </div>
      </div>

      {/* ── mobile search row (below main row) ── */}
      <div className="cn-search-row">
        <SearchBar {...searchBarProps} />
        {locationError && <div className="cn-loc-error">{locationError}</div>}
      </div>

      {/* ── mobile menu drawer ── */}
      {mobileMenuOpen && (
        <div className="cn-mobile-menu">
          {user && <div className="cn-mobile-name">👋 {user.fullName || user.userId}</div>}

          {/* Top Buttons */}
          <div className="cn-mobile-top-links">
            <button
              className="cn-mobile-top-btn"
              onClick={() => {
                navigate("/kmail");
                setMobileMenuOpen(false);
              }}
            >
              <FaEnvelope />
              <span>Mail</span>
            </button>

            <button
              className="cn-mobile-top-btn"
              onClick={() => {
                navigate("/kmail/Krivachat");
                setMobileMenuOpen(false);
              }}
            >
              <FaCommentDots />
              <span>Chat</span>
            </button>

            <button
              className="cn-mobile-top-btn"
              onClick={() => {
                navigate("/kmail/calls");
                setMobileMenuOpen(false);
              }}
            >
              <FaPhoneAlt />
              <span>Call</span>
            </button>
          </div>

          {user ? (
            <>
              <hr />

              {accountsLoading ? (
                <div className="cn-mobile-item">Loading accounts...</div>
              ) : (
                linkedAccounts.map((account) => (
                  <button
                    key={account.linkId || account.id}
                    className="cn-mobile-item"
                    onClick={() => handleSwitchAccount(account)}
                  >
                    {account.fullName || account.userId}
                    {account.userId === user?.userId && " (Active)"}
                  </button>
                ))
              )}

              <button className="cn-mobile-item cn-mobile-logout" onClick={handleLogout}>
                Logout
              </button>
            </>
          ) : (
            <>
              <button
                className="cn-mobile-login-btn"
                onClick={() => {
                  navigate("/login");
                  setMobileMenuOpen(false);
                }}
              >
                Login
              </button>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
